// Coupon management redesign (Phase 2 PR 6, 2026-05-15):
//   coupon_batch_codes 一覧 sheet (state filter + 簡易 pagination + soft delete)。
//
// Spec: design/coupon-management-redesign-spec.md §CMS 入稿 UX §2 Code 一覧
//
// 一覧 column: code / state / claimed_at / consumed_at / valid_to / 操作 (delete = soft delete、available のみ可)
// state filter: available / claimed / consumed / expired / (全件)
//
// pagination は backend GET /coupon-templates/:id/codes?limit=&offset= に乗っかる軽量版。
// 初期 limit = 100 (= 大抵の事業者の管理上限を 1 ページに表示)。

import { Button } from '@/components/common/Button';
import { Label } from '@/components/common/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import ApiService from '@/apis/ApiService';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';

// removeCode は本 PR で個別 API として stores に追加せず、ApiStore.call 経由で直接叩く
// (= DELETE /system/coupon-batch-codes/:id、Phase 1 PR 2 で実装済)。
// 個別 code は available のみ削除可 (backend が enforce、CouponTemplatesService.removeCode)。
class CouponBatchCodeDeleteApi extends ApiService {
    remove(id: number | string) {
        return this.delete(`coupon-batch-codes/${id}`);
    }
}

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    template: any | null,
    onChanged?: () => void
}

const STATE_FILTER_ALL = '__all__';
const STATE_OPTIONS: { value: string, label: string }[] = [
    { value: STATE_FILTER_ALL, label: '(全件)' },
    { value: 'available', label: 'available (= 未配布)' },
    { value: 'claimed', label: 'claimed (= 配布済 / 未使用)' },
    { value: 'consumed', label: 'consumed (= 使用済 / 焼却)' },
    { value: 'expired', label: 'expired (= 期限切れ)' }
];

const PAGE_SIZE = 100;

function formatDate(s: string | null | undefined): string {
    if (!s) return '-';
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return s;
    }
}

export default observer(function CouponBatchCodeListSheet({ open, onOpenChange, template, onChanged }: Props) {
    const {
        couponTemplateStore: { listCodes },
        apiStore,
        modalStore: { showAlertModal }
    } = useStore();

    const [items, setItems] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [stateFilter, setStateFilter] = useState<string>(STATE_FILTER_ALL);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        if (!template?.id) return;
        setLoading(true);
        try {
            const params: { state?: string, limit: number, offset: number } = {
                limit: PAGE_SIZE,
                offset
            };
            if (stateFilter !== STATE_FILTER_ALL) {
                params.state = stateFilter;
            }
            const res = await flowResult(listCodes({ id: template.id, params }));
            if (res?.ok) {
                setItems(res.data?.items ?? []);
                setTotal(res.data?.total ?? 0);
            } else if (res) {
                toastify('error', res?.message || 'コード一覧の取得に失敗しました');
            }
        } finally {
            setLoading(false);
        }
    }, [template?.id, stateFilter, offset, listCodes]);

    useEffect(() => {
        if (open) reload();
    }, [open, reload]);

    useEffect(() => {
        // template 切り替え / sheet 開きなおし時に offset / filter を初期化
        if (open) {
            setStateFilter(STATE_FILTER_ALL);
            setOffset(0);
        }
    }, [open, template?.id]);

    const handleDelete = (code: any) => {
        if (code.state !== 'available') {
            toastify('error', 'available 状態の code のみ削除可能');
            return;
        }
        showAlertModal({
            title: '削除確認',
            size: 'sm',
            content: `code "${code.code}" を削除しますか? (paranoid soft delete、復元可能)`,
            onSave: async () => {
                const api = new CouponBatchCodeDeleteApi();
                try {
                    const res = await apiStore.call(api, api.remove, code.id);
                    if (res?.ok) {
                        toastify('success', '削除しました');
                        reload();
                        onChanged?.();
                    } else {
                        toastify('error', res?.message || '削除に失敗しました');
                    }
                } catch (e: any) {
                    toastify('error', e?.message || '削除に失敗しました');
                }
            }
        });
    };

    const pageStart = total === 0 ? 0 : offset + 1;
    const pageEnd = Math.min(offset + PAGE_SIZE, total);
    const hasPrev = offset > 0;
    const hasNext = offset + PAGE_SIZE < total;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[800px] sm:max-w-[800px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>
                        コード一覧
                        {template?.name && (
                            <span className='text-sm font-normal text-gray-500 ml-2'>
                                (
                                {template.name}
                                )
                            </span>
                        )}
                    </SheetTitle>
                    <SheetDescription>
                        この template の coupon_batch_codes 一覧。state で絞り込み可。
                        available 状態の code のみ個別削除可能 (= 配布後の code は履歴保持のため削除不可)。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div className='flex items-center gap-3'>
                        <Label className='whitespace-nowrap'>state 絞り込み:</Label>
                        <div className='w-[280px]'>
                            <Select
                                value={stateFilter}
                                onValueChange={(v) => {
                                    setStateFilter(v);
                                    setOffset(0);
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder='選択してください' />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='ml-auto text-sm text-gray-600'>
                            {total === 0 ? '0 件' : `${pageStart}-${pageEnd} / ${total} 件`}
                        </div>
                    </div>

                    {loading ?
                        (
                            <div className='py-12 text-center text-gray-400 text-sm'>読み込み中…</div>
                        ) :
                        items.length === 0 ?
                            (
                                <div className='py-12 text-center text-gray-500 text-sm'>該当 code がありません</div>
                            ) :
                            (
                                <div className='overflow-x-auto border border-gray-200 rounded'>
                                    <table className='w-full text-xs'>
                                        <thead>
                                            <tr className='bg-gray-50 text-left border-b border-gray-200'>
                                                <th className='py-2 px-2'>ID</th>
                                                <th className='py-2 px-2'>code</th>
                                                <th className='py-2 px-2'>state</th>
                                                <th className='py-2 px-2'>claimed_at</th>
                                                <th className='py-2 px-2'>consumed_at</th>
                                                <th className='py-2 px-2'>valid_to</th>
                                                <th className='py-2 px-2 w-[60px]'>操作</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map(c => (
                                                <tr key={c.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                                    <td className='py-1.5 px-2 text-gray-500'>{c.id}</td>
                                                    <td className='py-1.5 px-2 font-mono'>{c.code}</td>
                                                    <td className='py-1.5 px-2'>
                                                        <StateBadge state={c.state} />
                                                    </td>
                                                    <td className='py-1.5 px-2 text-gray-600'>{formatDate(c.claimed_at)}</td>
                                                    <td className='py-1.5 px-2 text-gray-600'>{formatDate(c.consumed_at)}</td>
                                                    <td className='py-1.5 px-2 text-gray-600'>{formatDate(c.valid_to)}</td>
                                                    <td className='py-1.5 px-2'>
                                                        <Button
                                                            variant='outline'
                                                            size='sm'
                                                            disabled={c.state !== 'available'}
                                                            onClick={() => handleDelete(c)}
                                                            className='text-red-600 hover:text-red-800 disabled:opacity-40'
                                                            title={c.state === 'available' ? '削除' : '配布済 code は削除不可'}
                                                        >
                                                            <Trash2 className='w-3.5 h-3.5' />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                    {total > PAGE_SIZE && (
                        <div className='flex items-center justify-between text-sm'>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={!hasPrev || loading}
                                onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                            >
                                前へ
                            </Button>
                            <span className='text-gray-500'>
                                {pageStart}
                                -
                                {pageEnd}
                                {' '}
                                /
                                {' '}
                                {total}
                            </span>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={!hasNext || loading}
                                onClick={() => setOffset(o => o + PAGE_SIZE)}
                            >
                                次へ
                            </Button>
                        </div>
                    )}
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        閉じる
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});

const STATE_BADGE_CLS: Record<string, string> = {
    available: 'bg-emerald-100 text-emerald-700',
    claimed: 'bg-blue-100 text-blue-700',
    consumed: 'bg-gray-100 text-gray-700',
    expired: 'bg-amber-100 text-amber-700'
};

function StateBadge({ state }: { state: string }) {
    const cls = STATE_BADGE_CLS[state] ?? 'bg-gray-100 text-gray-600';
    return (
        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>
            {state}
        </span>
    );
}
